import React, { type PropsWithChildren, type Ref, useCallback, useImperativeHandle, useRef } from 'react'; // prettier-ignore
import { ScrollView, type ScrollViewInstance, StyleSheet } from 'react-native';
import Animated, { FadeIn, FadeOut } from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useStyle } from 'react-native-style-utilities';

import { theme } from '@garnish/constants';

import {
  BUNDLE_FOOTER_BUTTON_HEIGHT,
  BUNDLE_FOOTER_MIN_BOTTOM_SPACING,
  BUNDLE_TRANSITION_DURATION,
} from '../../Bundle.constants';

// ─────────────────────────────────────────────────────────────────────────────

export const BundleContent = (props: Props) => {
  const { children, ref } = props;

  const { bottom: safeAreaBottomInset } = useSafeAreaInsets();

  // ─── Refs ────────────────────────────────────────────────────────────

  const scrollViewRef = useRef<ScrollViewInstance>(null);
  const shouldScrollToEndRef = useRef(false);

  // ─── Handlers ────────────────────────────────────────────────────────

  // NOTE: Scrolling on content size change rather than on the state update, so
  //       the new row is laid out before we move.
  const handleContentSizeChange = useCallback(() => {
    if (!shouldScrollToEndRef.current) return;

    shouldScrollToEndRef.current = false;
    scrollViewRef.current?.scrollToEnd({ animated: true });
  }, []);

  // ─── Imperative Handle ───────────────────────────────────────────────

  // NOTE: Arms the scroll rather than performing it, since the row that
  //       triggered it has not been laid out yet.
  useImperativeHandle(
    ref,
    () => ({
      scrollToEnd: () => {
        shouldScrollToEndRef.current = true;
      },
    }),
    [],
  );

  // ─── Styles ──────────────────────────────────────────────────────────

  const contentContainerDynamicStyles = useStyle(
    () => ({
      paddingBottom:
        BUNDLE_FOOTER_BUTTON_HEIGHT +
        Math.max(BUNDLE_FOOTER_MIN_BOTTOM_SPACING, safeAreaBottomInset) +
        theme.spacing['6'],
    }),
    [safeAreaBottomInset],
  );

  const contentContainerStyles = [styles.contentContainer, contentContainerDynamicStyles];

  // ─────────────────────────────────────────────────────────────────────

  return (
    <Animated.View style={styles.container} entering={FADE_IN} exiting={FADE_OUT}>
      <ScrollView
        ref={scrollViewRef}
        contentContainerStyle={contentContainerStyles}
        onContentSizeChange={handleContentSizeChange}
      >
        {children}
      </ScrollView>
    </Animated.View>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const FADE_IN = FadeIn.duration(BUNDLE_TRANSITION_DURATION);
const FADE_OUT = FadeOut.duration(BUNDLE_TRANSITION_DURATION);

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  contentContainer: {
    paddingBottom: theme.spacing['6'],
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = PropsWithChildren<{
  ref?: Ref<BundleContentHandle>;
}>;

export type BundleContentHandle = {
  scrollToEnd: () => void;
};
